<template>
  <div class="space-y-4">
    <div class="bg-white border border-gray-200 rounded-lg p-4">
      <h3 class="text-sm font-semibold text-gray-800 mb-1">Generate Transcript for a Student</h3>
      <p class="text-xs text-gray-500 mb-4">Search for a student, select the transcript type and programme, then generate.</p>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <!-- Student Search -->
        <div class="sm:col-span-2">
          <label class="block text-xs font-medium text-gray-500 mb-1">Student *</label>
          <div class="relative">
            <InputText v-model="studentSearch" placeholder="Search by name or matric number..."
              size="small" class="w-full" @input="debouncedStudentSearch" />
            <div v-if="searchingStudents" class="absolute right-2 top-1/2 -translate-y-1/2">
              <i class="pi pi-spin pi-spinner text-gray-400 text-xs"></i>
            </div>
          </div>
          <!-- Search Results -->
          <div v-if="studentResults.length > 0 && !selectedStudent" class="mt-1 border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white shadow-sm">
            <button v-for="s in studentResults" :key="s.id" type="button"
              class="w-full text-left px-3 py-2 hover:bg-primary/10 border-b border-gray-50 last:border-0 flex items-center gap-2"
              @click="selectStudent(s)">
              <i class="pi pi-user text-gray-400 text-xs"></i>
              <div>
                <span class="text-sm font-medium">{{ s.surname }} {{ s.first_name }}</span>
                <span class="text-xs text-gray-400 ml-2 font-mono">{{ s.matric_no }}</span>
              </div>
            </button>
          </div>
          <!-- Selected Student -->
          <div v-if="selectedStudent" class="mt-1 flex items-center gap-2 px-3 py-2 bg-primary/10 border border-primary/20 rounded-lg">
            <i class="pi pi-user text-primary text-sm"></i>
            <div class="flex-1">
              <span class="text-sm font-semibold text-primary-700">{{ selectedStudent.surname }} {{ selectedStudent.first_name }}</span>
              <span class="text-xs text-primary ml-2 font-mono">{{ selectedStudent.matric_no }}</span>
            </div>
            <Button icon="pi pi-times" text rounded size="small" severity="secondary" @click="clearStudent" />
          </div>
        </div>

        <!-- Transcript Type -->
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Transcript Type</label>
          <Select v-model="selectedTypeId" :options="transcriptTypes" optionLabel="name" optionValue="id"
            placeholder="Select type" size="small" class="w-full" showClear />
        </div>

        <!-- Programme -->
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Programme</label>
          <ProgrammeSelect v-model="selectedProgrammeId" size="small" placeholder="Auto-detect" className="w-full" showClear />
        </div>
      </div>

      <div class="flex justify-end mt-4">
        <Button label="Generate Transcript" icon="pi pi-file" size="small"
          :loading="generating" :disabled="generating || !selectedStudent" @click="generate" />
      </div>
    </div>

    <!-- Generated Result -->
    <div v-if="generatedTranscript" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="flex items-center justify-between px-4 py-3 bg-green-50 border-b border-green-200">
        <div class="flex items-center gap-2">
          <i class="pi pi-check-circle text-green-600"></i>
          <span class="text-sm font-semibold text-green-800">Transcript Generated</span>
          <span v-if="generatedTranscript.transcriptTemplate" class="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
            {{ generatedTranscript.transcriptTemplate.name }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <Button label="Download PDF" icon="pi pi-download" size="small"
            :loading="downloading" :disabled="downloading" @click="downloadPdf" severity="success" />
        </div>
      </div>
      <TranscriptDataReviewPanel :data="generatedTranscript.data" :coverLetter="generatedTranscript.cover_letter" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useLazyQuery, useApolloClient } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { debounce } from '@/utils';
import gql from 'graphql-tag';
import ProgrammeSelect from '@/components/common/ProgrammeSelect.vue';
import TranscriptDataReviewPanel from './TranscriptDataReviewPanel.vue';
import { useTranscriptMutations } from '@/composables/transcript/useTranscriptMutations';
import { useTranscriptTypes } from '@/composables/transcript/useTranscriptTypes';
import { DOWNLOAD_TRANSCRIPT_PDF } from '@/graphql/transcript';
import type { Student } from '@/types/composition-functions';
import type { AdminGenerateTranscriptMutation } from '@/types/operations';
import { getErrorMessage } from '@/utils/error';

type StudentSearchOption = Pick<Student, 'id' | 'first_name' | 'surname' | 'matric_no'>;
type GeneratedTranscriptResult = AdminGenerateTranscriptMutation['adminGenerateTranscript'] & {
  transcriptTemplate?: { id: string; name: string } | null;
};

const toast = useToast();
const { adminGenerateTranscript, adminGenerating: generating } = useTranscriptMutations();
const { types: transcriptTypes } = useTranscriptTypes();
const { resolveClient } = useApolloClient();

const studentSearch = ref('');
const selectedStudent = ref<StudentSearchOption | null>(null);
const selectedTypeId = ref<string>();
const selectedProgrammeId = ref<string>();
const generatedTranscript = ref<GeneratedTranscriptResult | null>(null);
const downloading = ref(false);

// Student search
const SEARCH_STUDENTS = gql`
  query SearchStudents($search: String!, $first: Int!) {
    students(filter: { search: $search }, first: $first) {
      data { id first_name surname matric_no }
    }
  }
`;

const { load: loadStudents, result: studentSearchResult, loading: searchingStudents } = useLazyQuery(
  SEARCH_STUDENTS, undefined, { fetchPolicy: 'network-only' },
);

const studentResults = computed(() => studentSearchResult.value?.students?.data ?? []);

const debouncedStudentSearch = debounce(() => {
  if (studentSearch.value.trim().length >= 2) {
    selectedStudent.value = null;
    generatedTranscript.value = null;
    loadStudents(undefined, { search: studentSearch.value.trim(), first: 10 });
  }
}, 300);

function selectStudent(student: StudentSearchOption) {
  selectedStudent.value = student;
  studentSearch.value = `${student.surname} ${student.first_name}`;
}

function clearStudent() {
  selectedStudent.value = null;
  studentSearch.value = '';
  generatedTranscript.value = null;
}

async function generate() {
  if (!selectedStudent.value) return;
  try {
    const result = await adminGenerateTranscript({
      student_id: selectedStudent.value.id,
      transcript_type_id: selectedTypeId.value || undefined,
      programme_id: selectedProgrammeId.value || undefined,
    });
    if (result) {
      generatedTranscript.value = result as GeneratedTranscriptResult;
      toast.add({ severity: 'success', summary: 'Generated', detail: 'Transcript generated successfully.', life: 3000 });
    }
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: getErrorMessage(e), life: 5000 });
  }
}

async function downloadPdf() {
  if (!generatedTranscript.value) return;
  downloading.value = true;
  try {
    const client = resolveClient();
    const { data } = await client.query({
      query: DOWNLOAD_TRANSCRIPT_PDF,
      variables: {
        generated_transcript_id: generatedTranscript.value.id,
        category: selectedTypeId.value ? undefined : 'STUDENT_COPY',
      },
      fetchPolicy: 'no-cache',
    });

    const exportResult = data?.downloadTranscriptPdf;
    if (exportResult?.file_url) {
      window.open(exportResult.file_url, '_blank');
      toast.add({ severity: 'success', summary: 'PDF Ready', detail: 'Your transcript PDF is downloading.', life: 3000 });
    } else if (exportResult?.id) {
      toast.add({
        severity: 'info',
        summary: 'Export Started',
        detail: 'Your transcript PDF is being generated. Check the Downloads icon for progress.',
        life: 5000,
      });
    } else {
      toast.add({ severity: 'warn', summary: 'Could not start export', detail: 'Please try again.', life: 4000 });
    }
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Download Failed', detail: getErrorMessage(e), life: 5000 });
  } finally {
    downloading.value = false;
  }
}
</script>
